import React, { useState, useRef, useEffect } from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'
import Dialog from '@material-ui/core/Dialog'
import CloseIcon from '@material-ui/icons/Close'
import { useSelector, useDispatch } from 'react-redux'
import QRCode from 'qrcode'
import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'

import styles from './styles.module.scss'

import { hidePrintLabel } from '../../../store/asset/actions'
import { getAsset } from '../../../api'
import { generateAssetQrCode } from '../../../helpers/asset'

import Checkbox from '../../Core/Checkbox/Checkbox2'
import Select from '../Select'
import PdfLabel from '../PdfLabel'
import PrintLabelPreview from './PrintLabelPreview'

const PrintLabelModal = ({companyId}) => {
  const { t } = useTranslation()

  const dispatch = useDispatch()
  const { show, data} = useSelector((state) => state.asset.print)

  const [form, setForm] = useState({
    copies: 1,
    name: true,
    code: true,
    serial: true,
    category: true,
    manufacturer: true,
    output: 'label',
    layout: '70x36',
    template: '60x40',
  })
  const [pdfLabels, setPdfLabels] = useState([])
  const [qrCode, setQrCode] = useState(null)
  const [asset, setAsset] = useState(null)

  const pdfContainerRef = useRef(null)

  useEffect(() => {
    if (show && data) {
      getAsset(companyId, data.id).then(res => {
        if (res) {
          setAsset(res)
          generateAssetQrCode(res.qr_code_data)
            .then(setQrCode)
            .catch(error => {
              console.error('Failed to generate QR code', error)
              setQrCode(null)
            })
        }
      })
    }
  }, [show, data])

  const templateOptions = [
    {
      title: t('Small tool - 50 x 30 mm'),
      width: 50,
      height: 30,
      id: '50x30',
    }, {
      title: t('Standard - 60 x 40 mm'),
      width: 60,
      height: 40,
      id: '60x40',
    }, {
      title: t('Large Equipment - 80 x 50 mm'),
      width: 80,
      height: 50,
      id: '80x50',
    }
  ]

  const layoutOptions = [
    {
      title: 'A4 · 70 × 36 mm (3 × 8)',
      width: 70,
      height: 36,
      columns: 3,
      rows: 8,

      marginTop: 0,
      marginRight: 0,
      marginBottom: 0,
      marginLeft: 0,

      columnGap: 0,
      rowGap: 0,

      id: '70x36',
    },
    {
      title: 'A4 · 99 × 42 mm (2 × 6)',
      width: 99,
      height: 42,
      columns: 2,
      rows: 6,

      marginTop: 0,
      marginRight: 0,
      marginBottom: 0,
      marginLeft: 0,

      columnGap: 0,
      rowGap: 0,

      id: '99x42',
    },
    {
      title: 'A4 · 60 × 40 mm (3 × 6)',
      width: 60,
      height: 40,
      columns: 3,
      rows: 6,

      marginTop: 0,
      marginRight: 0,
      marginBottom: 0,
      marginLeft: 0,

      columnGap: 0,
      rowGap: 0,

      id: '60x40',
    },
  ]

  const currentLabelConfig = form.output === 'label'
    ? templateOptions.find(item => item.id === form.template)
    : layoutOptions.find(item => item.id === form.layout)

  const outputOptions = [
    {
      title: t('Label printer - one label {{width}} x {{height}} per page', {width: currentLabelConfig.width, height: currentLabelConfig.height}),
      id: 'label',
    }, {
      title: t('A4 adhesive sheet'),
      id: 'a4'
    }
  ]

  const habdleClose = () => {
    dispatch(hidePrintLabel())
  }

  const getFieldsConfig = () => ({
    name: form.name,
    code: form.code,
    serial: form.serial,
    category: form.category,
    manufacturer: form.manufacturer,
  })

  const getAssetData = () => {
    return {
      value: asset.qr_code_data,
      name: asset.name,
      code: asset.code,
      serial: asset.details.serial_number,
      category: asset.category.name,
      manufacturer: asset.details.manufacturer,
    }
  }

  const handleChangeCopies = (e) => {
    const value = e.target.value
    if (value === '' || /^[0-9\b]+$/.test(value)) {
      setForm({
        ...form,
        copies: value,
      })
    }
  }

  const generateLabelPdf = async ({
    asset,
    fields,
    qrCode,
  }) => {
    const template = templateOptions.find(
      item => item.id === form.template
    )

    if (!template) {
      return
    }

    const copies = Number(form.copies) || 1

    const pdf = new jsPDF({
      orientation:
        template.width > template.height
          ? 'landscape'
          : 'portrait',

      unit: 'mm',

      format: [
        template.width,
        template.height,
      ],

      compress: true,
    })

    for (let i = 0; i < copies; i++) {
      if (i > 0) {
        pdf.addPage(
          [
            template.width,
            template.height,
          ],
          template.width > template.height
            ? 'landscape'
            : 'portrait'
        )
      }

      const element = document.createElement('div')

      element.style.position = 'fixed'
      element.style.left = '-10000px'
      element.style.top = '0'

      document.body.appendChild(element)

      ReactDOM.render(
        <PdfLabel
          qrCode={qrCode}
          name={asset.name}
          code={asset.code}
          serial={asset.serial}
          category={asset.category}
          manufacturer={asset.manufacturer}
          width={template.width}
          height={template.height}
          fields={fields}
        />, element
      )

      await new Promise(resolve =>
        setTimeout(resolve, 50)
      )

      const canvas = await html2canvas(element, {
        scale: 3,
        backgroundColor: '#ffffff',
        useCORS: true,
      })

      const image = canvas.toDataURL(
        'image/png'
      )

      pdf.addImage(
        image,
        'PNG',
        0,
        0,
        template.width,
        template.height
      )

      ReactDOM.unmountComponentAtNode(element)
      document.body.removeChild(element)
    }

    pdf.save('asset-labels.pdf')
  }

  const generateA4Pdf = async ({
    asset,
    fields,
    qrCode,
  }) => {
    const layout = layoutOptions.find(
      item => item.id === form.layout
    )

    if (!layout) {
      return
    }

    const copies = Number(form.copies) || 1

    const labelsPerPage =
      layout.columns * layout.rows

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true,
    })

    for (let i = 0; i < copies; i++) {
      const position = i % labelsPerPage

      if (i > 0 && position === 0) {
        pdf.addPage('a4', 'portrait')
      }

      const column = position % layout.columns
      const row = Math.floor(position / layout.columns)

      const x =
        layout.marginLeft +
        column * (layout.width + layout.columnGap)

      const y =
        layout.marginTop +
        row * (layout.height + layout.rowGap)

      const element = document.createElement('div')

      element.style.position = 'fixed'
      element.style.left = '-10000px'
      element.style.top = '0'

      document.body.appendChild(element)

      ReactDOM.render(
        <PdfLabel
          qrCode={qrCode}
          name={asset.name}
          code={asset.code}
          serial={asset.serial}
          category={asset.category}
          manufacturer={asset.manufacturer}
          width={layout.width}
          height={layout.height}
          fields={fields}
        />, element
      )

      await new Promise(resolve => {
        setTimeout(resolve, 50)
      })

      const canvas = await html2canvas(element, {
        scale: 3,
        backgroundColor: '#ffffff',
        useCORS: true,
      })

      const image = canvas.toDataURL('image/png')

      pdf.addImage(
        image,
        'PNG',
        x,
        y,
        layout.width,
        layout.height
      )

      ReactDOM.unmountComponentAtNode(element)
      document.body.removeChild(element)
    }

    pdf.save('asset-labels.pdf')
  }

  const handleDownloadPdf = async () => {
    try {
      const asset = getAssetData()
      const fields = getFieldsConfig()

      const qrCode = await generateAssetQrCode(asset.value)

      if (form.output === 'label') {
        await generateLabelPdf({
          asset,
          fields,
          qrCode,
        })

        return
      }

      await generateA4Pdf({
        asset,
        fields,
        qrCode,
      })
    } catch (error) {
      console.error('Failed to generate PDF:', error)
    }
  }

  const handlePrintLabel = async () => {
    const config = currentLabelConfig
    const fields = getFieldsConfig()
    const asset = getAssetData()

    const qrCode = await QRCode.toDataURL(asset.value, {
      width: 300,
      margin: 0,
      errorCorrectionLevel: 'M',
    })

    const printWindow = window.open(
      '',
      '_blank',
      'width=800,height=600'
    )

    if (!printWindow) {
      return
    }

    const labels = Array.from(
      { length: Number(form.copies) || 1 },
      () => `
      <div class="label">
        <img
          class="qr"
          src="${qrCode}"
        />

        <div class="info">
          ${fields.name
          ? `<div class="name">${asset.name || ''}</div>`
          : ''
        }

          ${fields.code
          ? `<div>${asset.code || ''}</div>`
          : ''
        }

          ${fields.serial
          ? `<div>SN: ${asset.serial || ''}</div>`
          : ''
        }

          ${fields.category
          ? `<div>${asset.category || ''}</div>`
          : ''
        }

          ${fields.manufacturer
          ? `<div>${asset.manufacturer || ''}</div>`
          : ''
        }
        </div>
      </div>
    `
    ).join('')

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Asset labels</title>

          <style>
            @page {
              size: ${config.width}mm ${config.height}mm;
              margin: 0;
            }

            html,
            body {
              margin: 0;
              padding: 0;
            }

            .label {
              width: ${config.width}mm;
              height: ${config.height}mm;

              box-sizing: border-box;

              padding: 3mm;

              display: flex;
              align-items: center;
              gap: 3mm;

              background: white;

              page-break-after: always;
              break-after: page;

              font-family: Arial, sans-serif;
            }

            .qr {
              width: ${Math.min(config.height - 6, 30)}mm;
              height: ${Math.min(config.height - 6, 30)}mm;
              flex-shrink: 0;
            }

            .info {
              min-width: 0;
              overflow: hidden;

              font-size: 3mm;
              line-height: 1.25;
            }

            .name {
              font-weight: 600;
            }
          </style>
        </head>

        <body>
          ${labels}
        </body>
      </html>
    `)

    printWindow.document.close()

    setTimeout(() => {
      printWindow.focus()
      printWindow.print()
    }, 300)

  }

  const handlePrintA4 = async () => {
    const layout = layoutOptions.find(
      item => item.id === form.layout
    )

    if (!layout) {
      return
    }

    const fields = getFieldsConfig()
    const asset = getAssetData()

    const qrCode = await QRCode.toDataURL(asset.value, {
      width: 300,
      margin: 0,
      errorCorrectionLevel: 'M',
    })

    const copies = Number(form.copies) || 1

    const labelsPerPage = layout.columns * layout.rows

    const pageCount = Math.ceil(copies / labelsPerPage)

    const printWindow = window.open(
      '',
      '_blank',
      'width=1000,height=800'
    )

    if (!printWindow) {
      return
    }

    let pages = ''

    for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
      const start = pageIndex * labelsPerPage
      const end = Math.min(
        start + labelsPerPage,
        copies
      )

      let labels = ''

      for (let i = start; i < end; i++) {
        labels += `
        <div class="label">
          <img
            class="qr"
            src="${qrCode}"
          />

          <div class="info">
            ${fields.name
            ? `<div class="name">${asset.name || ''}</div>`
            : ''
          }

            ${fields.code
            ? `<div>${asset.code || ''}</div>`
            : ''
          }

            ${fields.serial
            ? `<div>SN: ${asset.serial || ''}</div>`
            : ''
          }

            ${fields.category
            ? `<div>${asset.category || ''}</div>`
            : ''
          }

            ${fields.manufacturer
            ? `<div>${asset.manufacturer || ''}</div>`
            : ''
          }
          </div>
        </div>
      `
      }

      pages += `
      <div class="page">
        ${labels}
      </div>
    `
    }

    printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Asset labels</title>

        <style>
          @page {
            size: A4 portrait;
            margin: 0;
          }

          html,
          body {
            margin: 0;
            padding: 0;
          }

          body {
            background: white;
          }

          .page {
            width: 210mm;
            height: 297mm;

            box-sizing: border-box;

            padding-top: ${layout.marginTop}mm;
            padding-right: ${layout.marginRight}mm;
            padding-bottom: ${layout.marginBottom}mm;
            padding-left: ${layout.marginLeft}mm;

            display: grid;

            grid-template-columns:
              repeat(
                ${layout.columns},
                ${layout.width}mm
              );

            grid-template-rows:
              repeat(
                ${layout.rows},
                ${layout.height}mm
              );

            column-gap: ${layout.columnGap}mm;
            row-gap: ${layout.rowGap}mm;

            page-break-after: always;
            break-after: page;

            overflow: hidden;
          }

          .label {
            width: ${layout.width}mm;
            height: ${layout.height}mm;

            box-sizing: border-box;

            padding: 3mm;

            display: flex;
            align-items: center;
            gap: 3mm;

            background: white;

            border: 0.2mm dashed #999;

            font-family: Arial, sans-serif;

            overflow: hidden;
          }

          .qr {
            width: ${Math.min(layout.height - 6, 30)}mm;
            height: ${Math.min(layout.height - 6, 30)}mm;

            flex-shrink: 0;
          }

          .info {
            min-width: 0;
            overflow: hidden;

            font-size: 3mm;
            line-height: 1.25;
          }

          .name {
            font-weight: 600;
          }
        </style>
      </head>

      <body>
        ${pages}
      </body>
    </html>
    `)

    printWindow.document.close()

    setTimeout(() => {
      printWindow.focus()
      printWindow.print()
    }, 300)
  }

  const handlePrint = () => {
    if (form.output === 'label') {
      handlePrintLabel()
    } else {
      handlePrintA4()
    }
  }

  // const asset = printLabel.data
  //   ? {
  //       value: printLabel.data.qrCode,
  //       name: printLabel.data.name,
  //       code: printLabel.data.code,
  //       serial: printLabel.data.details?.serial_number,
  //       category: printLabel.data.category?.name,
  //       manufacturer: printLabel.data.details?.manufacturer,
  //     }
  //   : null

  return (
    <Dialog
      onClose={habdleClose}
      open={show}
      maxWidth={'xl'}
      PaperProps={{ style: { maxWidth: 1080, width: '100%' } }}
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerTitle}>{t('Print asset label')}</div>
          <div className={styles.headerCloseButton} onClick={habdleClose}>
            <CloseIcon />
          </div>
        </div>
        <div className={styles.content}>
          <div className={styles.optionsContainer}>
            <div className={styles.section}>
              <div className={styles.sectionTitle}>{t('Copies')}</div>
              <div className={styles.copiesContainer}>
                <button type="button" className={styles.copiesButton} onClick={() => setForm(prev => ({ ...prev, copies: prev.copies - 1 }))}>-</button>
                <input type="text" className={styles.copiesInput} value={form.copies} onChange={handleChangeCopies} />
                <button type="button" className={styles.copiesButton} onClick={() => setForm(prev => ({ ...prev, copies: prev.copies + 1 }))}>+</button>
                <div className={styles.description}>{form.copies} {t('labels')}</div>
              </div>
            </div>
            <div className={styles.section}>
              <div className={styles.sectionTitle}>{t('Fields')}</div>
              <div className={styles.fieldsContainer}>
                <Checkbox label={t('Asset name')} checked={form.name} onChange={() => setForm(prev => ({ ...prev, name: !prev.name }))} />
                <Checkbox label={t('Asset code')} checked={form.code} onChange={() => setForm(prev => ({ ...prev, code: !prev.code }))} />
                <Checkbox label={t('Serial number')} checked={form.serial} onChange={() => setForm(prev => ({ ...prev, serial: !prev.serial }))} />
                <Checkbox label={t('Category')} checked={form.category} onChange={() => setForm(prev => ({ ...prev, category: !prev.category }))} />
                <Checkbox label={t('Manufacturer / model')} checked={form.manufacturer} onChange={() => setForm(prev => ({ ...prev, manufacturer: !prev.manufacturer }))} />
              </div>
            </div>
            <div className={styles.section}>
              <div className={styles.sectionTitle}>{t('Output')}</div>
              <Select options={outputOptions} value={form.output} onChange={(value) => setForm(prev => ({ ...prev, output: value }))} />
            </div>
            {
              form.output === 'a4'
                ? <div>
                  <div className={styles.sectionTitle}>{t('Sheet layout')}</div>
                  <Select options={layoutOptions} value={form.layout} onChange={(value) => setForm(prev => ({ ...prev, layout: value }))} />
                </div>
                : <div>
                  <div className={styles.sectionTitle}>{t('Template')}</div>
                  <Select options={templateOptions} value={form.template} onChange={(value) => setForm(prev => ({ ...prev, template: value }))} />
                </div>
            }
          </div>
          <div className={styles.previewContainer}>
            {qrCode && asset && (
              <PrintLabelPreview
                output={form.output}
                labelConfig={form.output === 'label' ? currentLabelConfig : null}
                layoutConfig={form.output === 'a4' ? currentLabelConfig : null}
                asset={asset}
                fields={getFieldsConfig()}
                qrCode={qrCode}
                copies={Number(form.copies) || 1}
              />
            )}
          </div>
        </div>
        <div className={styles.footer}>
          <button type="button" className={styles.cancelButton} onClick={habdleClose}>{t('Cancel')}</button>
          <button type="button" className={styles.pdfButton} onClick={handleDownloadPdf}>{t('Dwonload PDF')}</button>
          <button type="button" className={styles.printButton} onClick={handlePrint}>{t('Print')}</button>
        </div>
      </div>
      <div
        ref={pdfContainerRef}
        style={{
          position: 'fixed',
          left: '-10000px',
          top: 0,
          background: '#fff',
        }}
      >
        {pdfLabels}
      </div>
    </Dialog>
  )
}

export default PrintLabelModal
